import { useState, type FormEvent } from 'react';
import type { IdentityTrpcClient } from '../../lib/trpc';
import { trpc } from '../../lib/trpc';
import { Logo } from '../../components/Logo';
import { Button, Card, Field, Input, Notice } from '../../components/ui';
import { LanguageSelector } from '../../components/LanguageSelector';
import { localizedError, useI18n } from '../../lib/i18n';

export function InvitationPage({ client = trpc }: { client?: IdentityTrpcClient }) {
  const { t } = useI18n();
  const [token, setToken] = useState(new URLSearchParams(window.location.search).get('token') ?? '');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [seed, setSeed] = useState('');
  const [totp, setTotp] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  async function validateToken() {
    setSubmitting(true); setError('');
    try {
      const result = await client.auth.invitationTotpSetup.mutate({ token });
      setSeed(result.seed);
    } catch (caught) { setError(localizedError(caught, t)); }
    finally { setSubmitting(false); }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== repeat) { setError(t('publicPasswordsDoNotMatch')); return; }
    if (!seed) { setError(t('errorGeneric')); return; }
    setSubmitting(true); setError('');
    try {
      await client.auth.activateInvitation.mutate({ token, password, totpSeed: seed, totpCode: totp });
      setSeed(''); setTotp(''); setDone(true);
    } catch (caught) { setError(localizedError(caught, t)); }
    finally { setSubmitting(false); }
  }

  return <main className="auth-page"><div className="auth-brand"><Logo/><LanguageSelector/></div><Card className="auth-card auth-card-wide">{done
    ? <div className="success-panel"><span><svg viewBox="0 0 24 24"><path d="m5 12 4 4L19 6"/></svg></span><h1>{t('accountActivated')}</h1><p>{t('accountActivatedDescription')}</p><Button onClick={() => window.location.assign('/')}>{t('signIn')}</Button></div>
    : <><div className="auth-heading"><span className="step">{t('publicInvitationStep')}</span><h1>{t('invitationTitle')}</h1><p>{t('invitationDescription')}</p></div>
      {error && <Notice tone="danger">{error}</Notice>}
      <form className="form-grid" onSubmit={(event) => void submit(event)}>
        <Field label={t('invitationToken')} required><Input value={token} onChange={(event) => { setToken(event.target.value); setSeed(''); }} minLength={20} required readOnly={Boolean(seed)}/></Field>
        <div className="field seed-action"><span className="field-label">{t('validateToken')}</span><Button type="button" variant="secondary" disabled={token.length < 20 || submitting || Boolean(seed)} onClick={() => void validateToken()}>{seed ? t('invitationValidated') : t('validateInvitation')}</Button></div>
        {seed && <div className="full"><Field label={t('publicTotpKey')} hint={t('publicInvitationTotpHint')}><div className="secret-box"><code tabIndex={0}>{seed}</code><Button type="button" size="small" variant="secondary" onClick={() => void navigator.clipboard?.writeText(seed).then(() => setCopied(true))}>{copied ? t('copied') : t('copy')}</Button></div></Field></div>}
        <Field label={t('password')} required><Input type="password" autoComplete="new-password" minLength={12} value={password} onChange={(event) => setPassword(event.target.value)} required disabled={!seed}/></Field>
        <Field label={t('repeatPassword')} required><Input type="password" autoComplete="new-password" minLength={12} value={repeat} onChange={(event) => setRepeat(event.target.value)} required disabled={!seed}/></Field>
        <Field label={t('publicTotpCode')} required><Input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={totp} onChange={(event) => setTotp(event.target.value)} required disabled={!seed}/></Field>
        <div className="form-actions full"><Button type="submit" disabled={!seed || submitting}>{submitting ? t('loading') : t('activateAccount')}</Button></div>
      </form></>}
    </Card><p className="auth-footer">{t('protectedBy')}</p></main>;
}
